// src/careJobs/pages/JobBoardPage.tsx
import React from "react";
import {
  Box,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  InputBase,
  IconButton,
  Chip,
  Typography,
  Button,
  Alert,
  Grid,
  Stack,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import { Link as RouterLink, useLocation, useNavigate, useParams } from "react-router-dom"; // ⬅ add useNavigate
import { useQuery } from "@apollo/client";
import SeoMeta from "../../../../platform/components/common/seoMeta";

import { useSelectedBusiness } from "../../../../platform/redux/store";
import { GET_JOB_POSTINGS_BY_BUSINESS } from "../../../../platform/graphql/queries/careJobs";
import { GET_BUSINESS_BY_URL, GET_BUSINESS_CATEGORIES } from "../../../../platform/graphql/queries";
import JobCard, { JobCardJob } from "../components/jobCard";
import {
  GET_BUSINESSES_WITH_OPEN_JOBS,
  GET_SAVED_JOB_IDS,
} from "../../../../platform/graphql/queries/careJobs";
import useJobActions from "../hooks/useJobActions";

// ⬅ bring in the same auth context style you used elsewhere
import { UserContext } from "../../../../platform/components/auth/auth-context";

/* ---------------- config ---------------- */
const AFH_CATEGORY_NAME = "Adult Family Home";
const norm = (s?: string | null) => (s || "").trim().toLowerCase();

function makeAbs(p?: string): string {
  const s = String(p || "");
  if (!s) return "/";
  if (s.startsWith("/") || s.startsWith("http://") || s.startsWith("https://"))
    return s;
  return `/${s}`;
}

/* ---------------- small debounce helper ---------------- */
function useDebounce<T>(value: T, delay = 350) {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/* ================= props ================= */
export type JobBoardVariant = "two-pane" | "single";
export type JobBoardPageProps = {
  variant?: JobBoardVariant;
  businessId?: number | string | null;
  titleOverride?: string;
  enableSeoMeta?: boolean;

  hideModeToggle?: boolean;
  showBusinessAddress?: boolean;
  showRefresh?: boolean;
  showShare?: boolean;
  showApply?: boolean;
  showFacility?: boolean;
  showMyJob?: boolean;
  savedJobsPath?: string;
  myJobsPath?: string;
};

/* ---------------- page ---------------- */
export default function JobBoardPage({
  variant = "two-pane",
  businessId,
  titleOverride,
  enableSeoMeta = true,
  showBusinessAddress,
  showMyJob,
  showShare,
  showApply,
  showFacility,
  myJobsPath = "care/myjobs",
}: JobBoardPageProps) {
  const { businessUrl } = useParams<{ businessUrl?: string }>();
  const routeBusinessUrl = String(businessUrl || "").trim();
  const isBusinessJobsRoute = Boolean(routeBusinessUrl);
  const isSingle = variant === "single" || isBusinessJobsRoute;
  const navigate = useNavigate();
  const location = useLocation();
  const isCareJobsRoute = location.pathname.startsWith("/care/jobs");
  // ✅ Detect login (re-using your app’s UserContext pattern)
  const { providerUser, user } = React.useContext(UserContext) as any;
  const isLoggedIn = !!(providerUser?.email || user?.email); // adjust if your context differs

  // Default selection from store when no businessId prop
  const selected = useSelectedBusiness();
  const storeBizId = React.useMemo(() => {
    const n = Number(
      selected?.businessBasic?.id ?? (selected as any)?.businessId
    );
    return Number.isFinite(n) ? n : null;
  }, [selected]);

  const initialSelectedId = React.useMemo(() => {
    if (businessId != null) {
      const n = Number(businessId);
      if (Number.isFinite(n)) return n;
    }
    return storeBizId;
  }, [businessId, storeBizId]);

  const [search, setSearch] = React.useState("");
  const debouncedSearch = useDebounce(search, 350);
  const [selectedBusinessId, setSelectedBusinessId] = React.useState<
    number | null
  >(initialSelectedId);

  const {
    data: routeBusinessData,
    loading: routeBusinessLoading,
    error: routeBusinessError,
  } = useQuery(GET_BUSINESS_BY_URL, {
    variables: { businessUrl: routeBusinessUrl },
    skip: !routeBusinessUrl,
    fetchPolicy: "cache-and-network",
  });

  const routeBusiness = routeBusinessData?.businessByUrl;
  const routeBusinessId = React.useMemo(() => {
    const n = Number(routeBusiness?.id);
    return Number.isFinite(n) ? n : null;
  }, [routeBusiness?.id]);

  React.useEffect(() => {
    if (isBusinessJobsRoute) {
      setSelectedBusinessId(routeBusinessId);
      return;
    }
    if (isSingle) setSelectedBusinessId(initialSelectedId ?? null);
  }, [isSingle, isBusinessJobsRoute, routeBusinessId, initialSelectedId]);

  /* ========== 1) Categories -> find AFH category id (two-pane only) ========== */
  const {
    data: catsData,
    loading: catsLoading,
    error: catsError,
  } = useQuery(GET_BUSINESS_CATEGORIES, {
    fetchPolicy: "cache-first",
    skip: isSingle,
  });

  const { AFH_CATEGORY_ID, catsReady, categoryFound } = React.useMemo(() => {
    if (isSingle)
      return {
        AFH_CATEGORY_ID: null as string | null,
        catsReady: true,
        categoryFound: true,
      };
    if (catsLoading)
      return { AFH_CATEGORY_ID: null, catsReady: false, categoryFound: false };
    const cats = catsData?.getBusinessCategories ?? [];
    const match = cats.find(
      (c: any) => norm(c.name) === norm(AFH_CATEGORY_NAME)
    );
    return {
      AFH_CATEGORY_ID: match?.id ? String(match.id) : null,
      catsReady: true,
      categoryFound: !!match?.id,
    };
  }, [isSingle, catsLoading, catsData]);

  /* ========== 2) LEFT LIST: businesses that have open jobs ========== */
  const {
    data: idxData,
    loading: idxLoading,
    error: idxError,
    fetchMore,
  } = useQuery(GET_BUSINESSES_WITH_OPEN_JOBS, {
    variables: {
      categoryId: AFH_CATEGORY_ID as string,
      q: debouncedSearch || undefined,
      limit: 50,
      offset: 0,
    },
    skip: isSingle || !catsReady || !categoryFound,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "cache-and-network",
  });

  const idxItems: Array<any> = React.useMemo(
    () => idxData?.businessesWithOpenJobsByCategory?.items ?? [],
    [idxData]
  );
  const idxTotal: number =
    idxData?.businessesWithOpenJobsByCategory?.total ?? 0;

  React.useEffect(() => {
    if (!isSingle && !selectedBusinessId && idxItems.length > 0) {
      setSelectedBusinessId(Number(idxItems[0].id));
    }
  }, [isSingle, idxItems, selectedBusinessId]);

  const canLoadMore = !isSingle && categoryFound && idxItems.length < idxTotal;
  const loadMore = React.useCallback(async () => {
    if (!canLoadMore || !AFH_CATEGORY_ID) return;
    await fetchMore({
      variables: {
        categoryId: AFH_CATEGORY_ID,
        q: debouncedSearch || undefined,
        limit: 50,
        offset: idxItems.length,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        const prevList = prev?.businessesWithOpenJobsByCategory?.items ?? [];
        const nextBlock =
          fetchMoreResult?.businessesWithOpenJobsByCategory?.items ?? [];
        return {
          businessesWithOpenJobsByCategory: {
            total: fetchMoreResult.businessesWithOpenJobsByCategory.total,
            items: [...prevList, ...nextBlock],
            __typename:
              fetchMoreResult.businessesWithOpenJobsByCategory.__typename,
          },
        };
      },
    });
  }, [
    canLoadMore,
    AFH_CATEGORY_ID,
    fetchMore,
    idxItems.length,
    debouncedSearch,
  ]);

  /* ========== 3) RIGHT PANE: jobs for the selected business ========== */
  const {
    data: businessJobsData,
    loading: businessJobsLoading,
    error: businessJobsError,
  } = useQuery(GET_JOB_POSTINGS_BY_BUSINESS, {
    variables: {
      businessId: selectedBusinessId ? String(selectedBusinessId) : "",
      onlyActive: true,
      limit: 100,
      offset: 0,
    },
    skip: !selectedBusinessId,
    fetchPolicy: "cache-and-network",
  });

  const jobs: JobCardJob[] = businessJobsData?.jobPostingsByBusiness ?? [];

  /* ========== 4) Saved IDs (guarded by auth) ========== */
  const {
    data: savedIdsData,
    loading: savedIdsLoading,
    error: savedIdsError,
    refetch: refetchSavedIds,
  } = useQuery(GET_SAVED_JOB_IDS, {
    variables: { limit: 1000 },
    fetchPolicy: "cache-and-network",
    errorPolicy: "all", // ⬅ don’t blow up UI if server still returns an error
    skip: !isLoggedIn, // ⬅ key: avoid calling when logged out
  });

  const savedList = React.useMemo(
    () =>
      (isLoggedIn && !savedIdsError
        ? savedIdsData?.mySavedJobs ?? []
        : []) as { id: string }[],
    [isLoggedIn, savedIdsError, savedIdsData]
  );
  const savedIdSet = React.useMemo(() => {
    const s = new Set<string>();
    for (const j of savedList) if (j?.id != null) s.add(String(j.id));
    return s;
  }, [savedList]);

  // ⬅ Friendly login prompt and redirect helper
  const promptLogin = React.useCallback(() => {
    const returnTo = encodeURIComponent(
      window.location.pathname + window.location.search
    );
    navigate(`/login?returnTo=${returnTo}`);
  }, [navigate]);

  const { openApply, ApplyDialog, toggleSave } = useJobActions({
    afterSavedChange: async () => {
      // Only refetch if logged in (no-op otherwise)
      if (isLoggedIn) await refetchSavedIds();
    },
  });

  // Wrapper: if not logged in, send them to login instead of trying to save
  const onToggleSaveGuarded = React.useCallback(
    (job: JobCardJob, next: boolean) => {
      if (!isLoggedIn) {
        promptLogin();
        return;
      }
      toggleSave(job, next);
    },
    [isLoggedIn, toggleSave, promptLogin]
  );

  const leftLoading =
    !isSingle && (catsLoading || (categoryFound && idxLoading));

  const selectedBiz =
    !isSingle && selectedBusinessId
      ? idxItems.find((b: any) => Number(b.id) === selectedBusinessId)
      : undefined;

  const firstJobBusiness = jobs?.[0]?.business as any | undefined;
  const hasExplicitTitle = Boolean(titleOverride?.trim());
  const shouldShowHeaderTitle =
    !isSingle || hasExplicitTitle || isBusinessJobsRoute;
  const headerName =
    titleOverride ||
    selectedBiz?.name ||
    routeBusiness?.name ||
    firstJobBusiness?.name ||
    "Facility";
  const headerAddress =
    selectedBiz?.address ||
    routeBusiness?.address ||
    firstJobBusiness?.address ||
    undefined;
  const headerUrl =
    selectedBiz?.url || routeBusiness?.url || firstJobBusiness?.url || undefined;

  const MY_JOBS_TO = makeAbs(myJobsPath);
  const canShowMyJobsEntry = Boolean(showMyJob || isCareJobsRoute);
  const isBusinessOwner =
    isLoggedIn &&
    Number(selected?.businessBasic?.createdBy || 0) ===
      Number(providerUser?.userId || 0);
  const ownerPortalTo = isLoggedIn ? "/biz/manage/jobs?v=care" : "/biz/business";
  const onOpenMyJobsProfile = React.useCallback(() => {
    if (isLoggedIn) {
      navigate(MY_JOBS_TO);
      return;
    }
    navigate(`/login?returnTo=${encodeURIComponent(MY_JOBS_TO)}`);
  }, [isLoggedIn, navigate, MY_JOBS_TO]);
  const onOpenBrowseJobs = React.useCallback(() => {
    navigate("/care/jobs");
  }, [navigate]);
  const onOpenOwnerPortal = React.useCallback(() => {
    if (isLoggedIn) {
      navigate(ownerPortalTo);
      return;
    }
    navigate(`/login?returnTo=${encodeURIComponent(ownerPortalTo)}`);
  }, [isLoggedIn, navigate, ownerPortalTo]);
  const isBrowseJobsActive = isCareJobsRoute;
  const isMyJobsProfileActive = location.pathname.startsWith(MY_JOBS_TO);
  const isCaregiverFinderActive =
    location.pathname.startsWith("/biz/manage/jobs") ||
    location.pathname.startsWith("/biz/hiring") ||
    location.pathname.includes("/manage/jobs");
  const seoTitle =
    selectedBiz?.name || routeBusiness?.name || firstJobBusiness?.name
      ? `Care Jobs at ${selectedBiz?.name || routeBusiness?.name || firstJobBusiness?.name} | Plany365`
      : "Caregiving Jobs | Plany365";
  const seoDescription =
    selectedBiz?.name || routeBusiness?.name || firstJobBusiness?.name
      ? `Browse open caregiving and care staff jobs at ${selectedBiz?.name || routeBusiness?.name || firstJobBusiness?.name} on Plany365.`
      : "Browse open caregiving and care staff jobs on Plany365.";
  const seoUrl =
    typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}`
      : "/care/jobs";

  return (
    <Box
      sx={{ maxWidth: 1400, mx: "auto", px: { xs: 1.5, sm: 2, md: 3 }, py: 3 }}
    >
      {enableSeoMeta ? (
        <SeoMeta title={seoTitle} description={seoDescription} url={seoUrl} />
      ) : null}
      {isCareJobsRoute && (
        <Paper
          variant="outlined"
          sx={{
            mb: 2,
            p: { xs: 1.2, sm: 1.5 },
            borderRadius: 2,
            borderColor: "#dbe3ef",
            background: "linear-gradient(180deg, rgba(248,251,255,0.85) 0%, #fff 100%)",
          }}
        >
          <Stack spacing={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#1f3552" }}>
              Choose your portal
            </Typography>
            <RadioGroup
              row
              value={
                isCaregiverFinderActive
                  ? "finder"
                  : isMyJobsProfileActive
                    ? "myjobs"
                    : "browse"
              }
              onChange={(_, value) => {
                if (value === "finder") onOpenOwnerPortal();
                if (value === "myjobs") onOpenMyJobsProfile();
                if (value === "browse") onOpenBrowseJobs();
              }}
              sx={{
                p: { xs: 1, sm: 0.75 },
                borderRadius: 2,
                border: "1px solid #d7e2f2",
                background: "#f6f9ff",
                gap: { xs: 0.25, sm: 1.5 },
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <FormControlLabel
                value="browse"
                control={<Radio size="small" />}
                label="Jobs"
                sx={{
                  m: 0,
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 1.25,
                  bgcolor: isBrowseJobsActive ? "#e9f1ff" : "transparent",
                  border: "1px solid",
                  borderColor: isBrowseJobsActive ? "#c5d8fb" : "transparent",
                  ".MuiFormControlLabel-label": {
                    fontWeight: 700,
                    color: "#1f3552",
                  },
                }}
              />
              <FormControlLabel
                value="myjobs"
                control={<Radio size="small" />}
                label="My Jobs & Profile (Caregiver)"
                sx={{
                  m: 0,
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 1.25,
                  bgcolor: isMyJobsProfileActive ? "#e9f1ff" : "transparent",
                  border: "1px solid",
                  borderColor: isMyJobsProfileActive ? "#c5d8fb" : "transparent",
                  ".MuiFormControlLabel-label": {
                    fontWeight: 700,
                    color: "#1f3552",
                  },
                }}
              />
              {isBusinessOwner && (
                <FormControlLabel
                  value="finder"
                  control={<Radio size="small" />}
                  label="Business Hiring Portal (Create Jobs, Manage Jobs, Find Applicants)"
                  sx={{
                    m: 0,
                    px: 1.25,
                    py: 0.5,
                    borderRadius: 1.25,
                    bgcolor: isCaregiverFinderActive ? "#e9f1ff" : "transparent",
                    border: "1px solid",
                    borderColor: isCaregiverFinderActive ? "#c5d8fb" : "transparent",
                    ".MuiFormControlLabel-label": {
                      fontWeight: 700,
                      color: "#1f3552",
                    },
                  }}
                />
              )}
            </RadioGroup>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "flex-start", sm: "center" }}
              flexWrap="wrap"
            >
              <Chip size="small" color="success" variant="outlined" label="Search free" />
              <Chip size="small" color="success" variant="outlined" label="Apply free" />
              <Typography variant="body2" sx={{ color: "#48657a", flex: 1, minWidth: 240 }}>
                Browse and apply to care jobs for free. Sign in when you want saved
                jobs, a ready profile, and free job alerts.
              </Typography>
              <Button
                size="small"
                variant="outlined"
                component={RouterLink}
                to="/care/jobs/find"
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                Get Free Job Alerts
              </Button>
              <Button
                size="small"
                variant="outlined"
                component={RouterLink}
                to="/care/jobs/pricing"
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                See Pricing
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}
      <Grid container spacing={2}>
        {/* LEFT PANE — only in two-pane variant */}
        {!isSingle && (
          <Grid item xs={12} md={4} lg={3}>
            <Paper
              variant="outlined"
              sx={{ p: 1.25, borderRadius: 2, position: "sticky", top: 12 }}
            >
              {/* Search */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1,
                  py: 0.25,
                  mb: 1,
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <SearchIcon fontSize="small" />
                <InputBase
                  placeholder="Search jobs or facilities…"
                  fullWidth
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ py: 0.5, fontSize: 14 }}
                />
                {search && (
                  <IconButton
                    size="small"
                    onClick={() => setSearch("")}
                    aria-label="Clear search"
                  >
                    <SearchIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              {/* State: loading / error / empty */}
              {catsError || idxError ? (
                <Alert severity="error" sx={{ my: 1 }}>
                  {String(
                    (catsError as any)?.message ||
                    (idxError as any)?.message ||
                    "Failed to load."
                  )}
                </Alert>
              ) : !catsReady ? (
                <Typography sx={{ px: 1, py: 0.5 }} variant="body2">
                  Loading…
                </Typography>
              ) : catsReady && !categoryFound ? (
                <Alert severity="info">
                  We couldn’t find a category named “{AFH_CATEGORY_NAME}”.
                </Alert>
              ) : leftLoading && idxItems.length === 0 ? (
                <Typography sx={{ px: 1, py: 0.5 }} variant="body2">
                  Loading…
                </Typography>
              ) : idxItems.length === 0 ? (
                <Alert severity="info">
                  No Adult Family Homes with open jobs.
                </Alert>
              ) : (
                <>
                  <List
                    dense
                    disablePadding
                    sx={{ maxHeight: "calc(100vh - 260px)", overflowY: "auto" }}
                  >
                    {idxItems.map((b: any) => {
                      const selectedRow = selectedBusinessId === Number(b.id);
                      return (
                        <React.Fragment key={b.id}>
                          <ListItemButton
                            selected={selectedRow}
                            onClick={() => setSelectedBusinessId(Number(b.id))}
                            sx={{
                              borderRadius: 1,
                              mb: 0.25,
                              ...(selectedRow
                                ? { bgcolor: "action.selected" }
                                : {}),
                            }}
                          >
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: "primary.light" }}>
                                <BusinessRoundedIcon />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  <Typography
                                    variant="subtitle2"
                                    noWrap
                                    fontWeight={700}
                                    title={b.name}
                                  >
                                    {b.name}
                                  </Typography>
                                  <Chip
                                    size="small"
                                    label={String(b.jobCount)}
                                  />
                                </Stack>
                              }
                              secondary={
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  noWrap
                                  title={b.address || ""}
                                >
                                  {b.address || "—"}
                                </Typography>
                              }
                            />
                          </ListItemButton>
                          <Divider component="li" />
                        </React.Fragment>
                      );
                    })}
                  </List>

                  {canLoadMore && (
                    <Box sx={{ mt: 1, textAlign: "center" }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={loadMore}
                        disabled={idxLoading}
                      >
                        {idxLoading ? "Loading…" : "Load more"}
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </Paper>
          </Grid>
        )}

        {/* RIGHT: Job list for selected business */}
        <Grid item xs={12} md={isSingle ? 12 : 8} lg={isSingle ? 12 : 9}>
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 1.25, sm: 1.6 },
              borderRadius: 2.5,
              borderColor: "#dbe3ef",
              background:
                "linear-gradient(180deg, rgba(248,251,255,0.9) 0%, rgba(255,255,255,1) 30%)",
              boxShadow: "0 6px 18px rgba(17, 38, 73, 0.06)",
            }}
          >
            {/* Header */}
            <Box sx={{ mb: 1.5 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                spacing={1}
              >
                <Box sx={{ minWidth: 0 }}>
                  {shouldShowHeaderTitle && (
                    <Typography
                      variant="h5"
                      fontWeight={800}
                      noWrap
                      title={headerName}
                      sx={{ color: "#1f3552", letterSpacing: 0.1 }}
                    >
                      {headerName}
                    </Typography>
                  )}
                  {!!headerAddress && showBusinessAddress && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      noWrap
                      title={headerAddress}
                      sx={{ mt: 0.25 }}
                    >
                      {headerAddress}
                    </Typography>
                  )}
                </Box>

                {/* Top-right actions */}
                <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
                  {isBusinessJobsRoute && (
                    <Button
                      size="small"
                      variant="outlined"
                      component={RouterLink}
                      to="/care/jobs"
                      sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700 }}
                    >
                      Job Search Board
                    </Button>
                  )}
                  {canShowMyJobsEntry && (isLoggedIn ? (
                    <Button
                      size="small"
                      variant="contained"
                      component={RouterLink}
                      to={MY_JOBS_TO}
                      sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700 }}
                    >
                      My Jobs & Profile
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const returnTo = encodeURIComponent(MY_JOBS_TO);
                        navigate(`/login?returnTo=${returnTo}`);
                      }}
                      sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700 }}
                    >
                      Sign in for My Jobs
                    </Button>
                  ))}


                  {!!headerUrl && !!showFacility && (
                    <Button
                      size="small"
                      variant="outlined"
                      component={RouterLink}
                      to={`/b/${headerUrl}`}
                      startIcon={<OpenInNewRoundedIcon />}
                      sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700 }}
                    >
                      View Facility
                    </Button>
                  )}
                </Stack>
              </Stack>
              {routeBusinessError ? (
                <Alert severity="error" sx={{ mt: 1.2 }}>
                  {String((routeBusinessError as any)?.message || routeBusinessError)}
                </Alert>
              ) : isBusinessJobsRoute && routeBusinessLoading && !routeBusiness ? (
                <Typography sx={{ mt: 1.2 }} variant="body2">
                  Loading facility...
                </Typography>
              ) : isBusinessJobsRoute && !routeBusiness ? (
                <Alert severity="info" sx={{ mt: 1.2 }}>
                  We could not find this facility.
                </Alert>
              ) : !selectedBusinessId && (
                <Alert severity="info" sx={{ mt: 1.2 }}>
                  {isSingle
                    ? "No business selected."
                    : "Select a facility on the left to view its open roles."}
                </Alert>
              )}
            </Box>

            <Divider sx={{ mb: 1.5, borderColor: "#e2e8f2" }} />

            {/* Jobs */}
            {!selectedBusinessId ? null : businessJobsError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {String((businessJobsError as any)?.message || businessJobsError)}
              </Alert>
            ) : businessJobsLoading && (jobs?.length ?? 0) === 0 ? (
              <Typography>Loading…</Typography>
            ) : (jobs?.length ?? 0) === 0 ? (
              <Alert severity="info">
                No open positions for this facility right now.
              </Alert>
            ) : (
              <Grid container spacing={1.25}>
                {jobs.map((j: JobCardJob) => {
                  const hideSaveWhileUnknown =
                    (isLoggedIn && savedIdsLoading) ||
                    (!!savedIdsError && isLoggedIn);
                  const isSaved =
                    isLoggedIn &&
                    !hideSaveWhileUnknown &&
                    savedIdSet.has(String(j.id));

                  return (
                    <Grid key={j.id as React.Key} item xs={12}>
                      <JobCard
                        job={j}
                        showShare={showShare}
                        // when logged out, still show Save → it will route to login
                        showSave={!hideSaveWhileUnknown && !isSaved}
                        isSaved={isSaved}
                        savedKnown={!hideSaveWhileUnknown}
                        showApply={showApply}
                        onApply={openApply}
                        onToggleSave={onToggleSaveGuarded} // ⬅ guarded
                      />
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Paper>

          {ApplyDialog}
        </Grid>
      </Grid>
    </Box>
  );
}

